import CollapsibleSectionPanel from "components/CollapsibleSectionPanel";
import { EmptystateVariablesColored } from "components/icons/generated";
import PageWrapper from "components/PageWrapper";
import PageInfo from "components/PageWrapper/Info";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import useTypedContext from "hooks/useTypedContext";
import { ConfigElementWithSource, StackContextAttachment } from "types/generated";

import { showEnvVariableFormDrawer } from "../components/EnvVariablesDrawer/EnvVariablesFormDrawer";
import { showMountedFilesFormDrawer } from "../components/MountedFileDrawer/MountedFilesFormDrawer";
import EnvVariableActionsCell from "../components/Table/Cells/EnvVariableActionsCell";
import MountedFilesActionsCell from "../components/Table/Cells/MountedFilesActionsCell";
import EnvironmentTable from "../components/Table/EnvironmentTable";
import ContextsRow from "../components/Table/Rows/ContextsRow";
import FilesRow from "../components/Table/Rows/FilesRow";
import VariablesRow from "../components/Table/Rows/VariablesRow";
import { ContextColumnId, CONTEXTS_COLUMNS } from "../constants/contextTable";
import { ENV_VARS_COLUMNS, EnvVariablesColumnId } from "../constants/envVariableTable";
import { CUSTOM_END_COLUMN_CONFIG } from "../constants/globalTable";
import { FilesColumnId, MOUNTED_FILES_COLUMNS } from "../constants/mountedFilesTable";
import { SPACELIFT_VARS_COLUMNS } from "../constants/spaceliftVarsTable";
import { EnvironmentContext } from "../EnvironmentContext";

type EnvVariable = ConfigElementWithSource & { id: string };

type EnvironmentPageProps = {
  localVariables: EnvVariable[];
  mountedFiles: EnvVariable[];
  spaceliftVariables: EnvVariable[];
  attachedContexts: StackContextAttachment[];
};

const EnvironmentPage = ({
  localVariables,
  mountedFiles,
  spaceliftVariables,
  attachedContexts,
}: EnvironmentPageProps) => {
  const { canCreateAndEditVariables, onCreate } = useTypedContext(EnvironmentContext);

  return (
    <>
      <PageInfo title="Environment" />
      <PageWrapper margin="0" padding="0" gap="x-large">
        <CollapsibleSectionPanel
          ariaLevel={3}
          title="Environment Variables"
          initialIsCollapsed={false}
          count={localVariables.length}
          actions={
            canCreateAndEditVariables ? (
              <Button
                variant="secondary"
                onPress={() =>
                  showEnvVariableFormDrawer({
                    onSubmit: onCreate,
                  })
                }
              >
                Add Variable
              </Button>
            ) : undefined
          }
          contentPadding="0"
        >
          <EnvironmentTable
            columnsConfig={ENV_VARS_COLUMNS}
            endCustomColumns={canCreateAndEditVariables ? CUSTOM_END_COLUMN_CONFIG : null}
            emptyTable={
              <EmptyState
                padding="x-large 0"
                icon={EmptystateVariablesColored}
                title="You do not have any variables yet"
                caption="Environment variables are represented as a key-value mapping available to all processes running in a given environment. Click on the button above to create a new variable."
              />
            }
            items={localVariables}
            rowRenderer={(item, key) => (
              <VariablesRow
                key={key}
                config={item}
                canCreateAndEditVariables={canCreateAndEditVariables}
                actionsComponent={canCreateAndEditVariables ? EnvVariableActionsCell : undefined}
              />
            )}
            rowHeaderId={EnvVariablesColumnId.CREATED_AT}
            tableId="stack-env-variables"
          />
        </CollapsibleSectionPanel>
        <CollapsibleSectionPanel
          ariaLevel={3}
          title="Mounted files"
          count={mountedFiles.length}
          actions={
            canCreateAndEditVariables ? (
              <Button
                variant="secondary"
                onPress={() =>
                  showMountedFilesFormDrawer({
                    onSubmit: onCreate,
                  })
                }
              >
                Add file
              </Button>
            ) : undefined
          }
          contentPadding="0"
        >
          <EnvironmentTable
            columnsConfig={MOUNTED_FILES_COLUMNS}
            endCustomColumns={canCreateAndEditVariables ? CUSTOM_END_COLUMN_CONFIG : null}
            emptyTable={
              <EmptyState
                padding="x-large 0"
                icon={EmptystateVariablesColored}
                title="There are no mounted files for this environment"
                caption="Mounted files are represented as a key-value mapping available to all processes running in a given environment."
              />
            }
            items={mountedFiles}
            rowRenderer={(item, key) => (
              <FilesRow
                key={key}
                config={item}
                canCreateAndEditVariables={canCreateAndEditVariables}
                actionsComponent={canCreateAndEditVariables ? MountedFilesActionsCell : undefined}
              />
            )}
            rowHeaderId={FilesColumnId.CREATED_AT}
            tableId="stack-mounted-files"
          />
        </CollapsibleSectionPanel>
        <CollapsibleSectionPanel
          ariaLevel={3}
          title="Spacelift environment"
          count={spaceliftVariables.length}
          contentPadding="0"
        >
          <EnvironmentTable
            columnsConfig={SPACELIFT_VARS_COLUMNS}
            emptyTable={
              <EmptyState
                padding="x-large 0"
                icon={EmptystateVariablesColored}
                title="There are no Spacelift variables for this environment"
                caption="Environment variables are represented as a key-value mapping available to all processes running in a given environment."
              />
            }
            items={spaceliftVariables}
            rowRenderer={(item, key) => (
              <VariablesRow
                key={key}
                config={item}
                canCreateAndEditVariables={canCreateAndEditVariables}
                actionsComponent={() => undefined}
              />
            )}
            rowHeaderId={EnvVariablesColumnId.NAME}
            tableId="stack-spacelift-env-variables"
          />
        </CollapsibleSectionPanel>

        {attachedContexts.length ? (
          <CollapsibleSectionPanel
            ariaLevel={3}
            title="Attached contexts"
            count={attachedContexts.length}
            contentPadding="0"
          >
            <EnvironmentTable
              columnsConfig={CONTEXTS_COLUMNS}
              items={attachedContexts}
              rowRenderer={(item, key) => (
                <ContextsRow
                  key={key}
                  context={item}
                  canCreateAndEditVariables={canCreateAndEditVariables}
                />
              )}
              rowHeaderId={ContextColumnId.NAME}
              tableId="stack-attached-contexts"
            />
          </CollapsibleSectionPanel>
        ) : null}
      </PageWrapper>
    </>
  );
};

export default EnvironmentPage;
